import React from 'react';
import { Container, Row, Col, Form, Button, Table,
         Accordion, Card, ProgressBar, Alert } from 'react-bootstrap';

// Components
import { authService } from '../../../_services/auth';
import { DiscordBot }  from './DiscordBot';

// Utilities
import MetaDecorator from '../../../_helpers/MetaDecorator';

// Styles
import './Dashboard.scss';

export default class Dashboard extends React.Component {
  render() {
    return (
      <Container className="admin-component pt-3">
        <MetaDecorator title="Dashboard" />
        <authService.userContext.Consumer>
          {({user}) => {
            if (!user) return null
            return (
              <Row>
                <Col md={12}>
                  <h1>
                    Hello {user.name}
                    <small className="text-muted">{user.role}</small>
                  </h1>
                </Col>
                { (user.role === 'admin' || user.role === 'staff') &&
                <>
                  <CurveUpdater />
                  <ModifierTypeLinksUpdater />
                  <SDDUpdater />
                </>
                }
                { user.role === 'admin' &&
                <DiscordBot />
                }
              </Row>
            )
          }}
        </authService.userContext.Consumer>
    </Container>
    )
  }
}

class UpdateTemplate extends React.Component {
  constructor(props) {
    super(props);

    this.state = { }
  }

  upload = e => {
    this.setState({ error: '', success: '' });

    // Create data to send
    var input = document.querySelector(`#${this.input}`);
    var data = new FormData();
    data.append('file', input.files[0]);

    authService.authFetch(this.endpoint, {
      method: 'POST',
      body: data
    })
    .then(res => {
      if (res.status === 500 || res.status === 400) {
        this.setState({ error: "Something went wrong... check file." })
      } else {
        return res.text();
      }
    })
    .then(data => this.setState({ success: data }));
  }

  handleFileChange = e => {
    const file = e.target.files[0];
    
    if (file) {
      this.setState({
        selectedFileName: file.name
      });
      
      // Optional: store the file itself if needed for upload
      this.selectedFile = file;
    } else {
      this.setState({ selectedFileName: '' });
    }
  }
}

class CurveUpdater extends UpdateTemplate {
  constructor(props) {
    super(props);

    this.endpoint = '/admin/upload/curves';
    this.input = 'fileCurve';
  }

  render() {
    const { selectedFileName } = this.state;

    return (
      <Col className="dashboard-column" md={6}>
        <small className="text-muted">Curve File (.T3D)</small>
        <Form>
          <Form.File 
            id={this.input} 
            label={selectedFileName || "..."} 
            custom 
            onChange={this.handleFileChange}
            accept=".t3d"
          />
          <Button variant="primary" onClick={this.upload}>
            Submit
          </Button>
          <span className="text-success">{this.state.success}</span>
          <span className="text-danger">{this.state.error}</span>
        </Form>
      </Col>
    )
  }
}

class SDDUpdater extends UpdateTemplate {
  constructor(props) {
    super(props);

    this.endpoint = '/admin/upload/sdd';
    this.input = 'fileSdd';
    this.state = { job: null };

    this.upload = this.upload.bind(this);
  }

  componentDidMount() {
    // Check for (and resume watching) an already-running import, e.g. after
    // navigating away and back — the job lives on the server, not in this
    // component, so it should be visible regardless of when/why this page
    // was loaded.
    this.pollStatus();
    this.pollInterval = setInterval(() => this.pollStatus(), 1000);
  }

  componentWillUnmount() {
    clearInterval(this.pollInterval);
  }

  pollStatus = () => {
    authService.authFetch('/admin/dashboard/sdd/status')
      .then(res => res.json())
      .then(job => this.setState({ job }))
      .catch(() => {});
  };

  upload = e => {
    this.setState({ error: '', success: '' });

    const fileInput = document.querySelector(`#${this.input}`);
    const file = fileInput?.files[0];

    if (!file) {
      this.setState({ error: "Please select a file" });
      return;
    }

    const form = fileInput.closest('form');
    const version = form?.querySelector('input[type="text"]')?.value.trim();

    if (!version) {
      this.setState({ error: "Please enter a version number" });
      return;
    }

    const queryParams = new URLSearchParams({
      filename: file.name,
      version: version
    });

    const url = `${this.endpoint}?${queryParams}`;

    console.log("Uploading to:", url);   // for debugging

    authService.authFetch(url, {
      method: 'POST',
      body: file,                                 // ← Send raw file
      headers: {
        'Content-Type': 'application/octet-stream'
      }
    })
    .then(res => {
      console.log("Response status:", res.status);
      if (!res.ok) {
        return res.text().then(text => { throw new Error(text || res.statusText); });
      }
      return res.text();
    })
    .then(() => {
      this.setState({ success: `Version ${version} imported successfully!` });
    })
    .catch(err => {
      console.error("Upload error:", err);
      this.setState({ error: "Upload failed - " + err.message });
    })
    .finally(() => {
      // The upload request settling is the authoritative "done" signal for
      // this uploader's own view — don't leave whatever the last poll
      // happened to capture on screen. Polling itself keeps running (it's
      // tied to the component's lifetime, not this one upload) so anyone
      // else watching the dashboard still sees the real state either way.
      this.setState({ job: null });
    });
  };

  render() {
    const { selectedFileName, job } = this.state;

    return (
      <Col className="dashboard-column" md={6}>
        <small className="text-muted">sdd.bin (.BIN)</small>
        <Form>
          <Form.File
            id={this.input}
            label={selectedFileName || "..."}
            custom
            onChange={this.handleFileChange}
            accept=".bin"
          />
          <Form.Control type="text" placeholder="1.32.0.849645" />
          <Button variant="primary" onClick={this.upload}>
            Submit
          </Button>
          <span className="text-success">{this.state.success}</span>
          <span className="text-danger">{this.state.error}</span>
        </Form>
        {job && job.status === 'running' &&
          <div className="mt-2">
            <ProgressBar
              now={job.percentage}
              label={`${job.percentage}%`}
              animated={job.phase !== 'inserting'}
            />
            <small className="text-muted d-block">
              {job.phase === 'comparing' && `Comparing ${job.currentTable} (${job.tableIndex}/${job.totalTables})...`}
              {job.phase === 'creating' && `Creating ${job.currentTable} (${job.tableIndex}/${job.totalTables})...`}
              {job.phase === 'inserting' && `Importing ${job.currentTable}: ${job.rowsProcessed}/${job.totalRows} rows (${job.tableIndex}/${job.totalTables} tables)`}
            </small>
          </div>
        }
        {job && job.status === 'error' &&
          <Alert variant="danger" className="mt-2">
            Import failed: {job.error}
          </Alert>
        }
      </Col>
    )
  }
}

class ModifierTypeLinksUpdater extends UpdateTemplate {
  constructor(props) {
    super(props);

    this.endpoint = '/admin/upload/modifiers';
    this.input = 'fileModifiers';

    this.state = { modifiers: [] }
    this.loadModifiers = this.loadModifiers.bind(this);
  }

  loadModifiers() {
    authService.authenticatedRequest('/admin/dashboard/modifiers')
      .then(data => this.setState({ modifiers: data }));
  }

  render() {
    const { selectedFileName } = this.state;

    return (
      <Col className="dashboard-column" md={6}>
        <small className="text-muted">Modifier Type File</small>
        <Form>
          <Form.File 
            id={this.input} 
            label={selectedFileName || "..."} 
            custom 
            onChange={this.handleFileChange}
          />
          <Button variant="primary" onClick={this.upload}>
            Submit
          </Button>
          <span className="text-success">{this.state.success}</span>
          <span className="text-danger">{this.state.error}</span>
        </Form>

        <Accordion className="pt-3" onSelect={this.loadModifiers}>
          <Card>
            <Accordion.Toggle as={Card.Header} eventKey="0">
              Modifier List
            </Accordion.Toggle>
            <Accordion.Collapse eventKey="0">
              <Card.Body>
                <Table className="table-lite">
                  <tbody>
                    {this.state.modifiers.map(m => (
                      <tr key={m.eModifierType} className="small">
                        <td>{m.eModifierType}</td>
                        <td>{m.sTable}</td>
                        <td>{m.sVariable}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
      </Col>
    )
  }
}
